import { Box, Divider, Link, Typography } from '@mui/material';
import Image from 'next/image';
import { landingAssets } from '../landing-assets';
import { landingContent } from '../landing-content';
import { landingPalette } from '../landing-theme';
import { SectionShell } from './SectionShell';

/**
 * Footer: Attend mark, product name, descriptor and the institutional legal links.
 *
 * The `xs` layout is a plain top-to-bottom stack (brand → descriptor → hairline divider →
 * single-column links → copyright) with a deliberate brightness taper — descriptor brighter
 * than the base grey, links at the base grey, copyright dimmed further — so the block reads
 * as hierarchy rather than one undifferentiated grey mass. `sm` and up keep the original
 * two-cluster row, unchanged.
 */
export function LandingFooter() {
  return (
    <SectionShell
      component="footer"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        justifyContent: 'space-between',
        gap: { xs: 4, sm: 3 },
        py: 8,
        color: '#8d8d8d',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
          <Image
            src={landingAssets.attendLogo}
            alt={landingContent.brandLogoAlt}
            width={30}
            height={24}
          />
          <Typography sx={{ fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase' }}>
            {landingContent.footer.brand}
          </Typography>
        </Box>
        <Typography sx={{ color: { xs: 'text.secondary', sm: 'inherit' } }}>
          {landingContent.footer.description}
        </Typography>
      </Box>
      <Divider
        sx={{
          display: { xs: 'block', sm: 'none' },
          width: '100%',
          borderColor: landingPalette.line,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'flex-start', sm: 'flex-end' },
          gap: 1.5,
        }}
      >
        <Box
          component="nav"
          aria-label={landingContent.sectionLabels.footerNav}
          sx={{
            display: { xs: 'grid', sm: 'flex' },
            // The four labels exceed the cluster's width around 900px; without wrapping they
            // overflow their column instead of folding onto a second line.
            flexWrap: 'wrap',
            justifyContent: { sm: 'flex-end' },
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          {landingContent.footer.links.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: 'inherit', fontSize: '.9rem' }}
            >
              {link.label}
            </Link>
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: { xs: '.8rem', sm: '.85rem' },
            color: '#7a7a7a',
            textAlign: { xs: 'left', sm: 'right' },
          }}
        >
          {landingContent.footer.copyright}
        </Typography>
      </Box>
    </SectionShell>
  );
}
